import React, { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "@shopify/polaris";
import Cropper, { Area } from "react-easy-crop";
import { toast } from "sonner";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { upload, isEmptyUploadError } from "services/upload-file.service";
import { setUserAvatar } from "services/user-avatar.service";
import { fetchUserDetails } from "containers/user/user-container.slice";
import { fetchOnlineThunk } from "features/presence/presence.slice";
import { AppDispatch } from "configureStore";
import { getCroppedBlob } from "./get-cropped-blob";

interface AvatarUploadModalProps {
  companyId: string;
  userId: string;
}

const AVATAR_SIZE = 512;

const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  companyId,
  userId,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(t("avatar_must_be_image"));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t("avatar_file_too_large"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels, AVATAR_SIZE);
      const cropFile = new File([blob], `avatar-${Date.now()}.jpg`, { type: "image/jpeg" });
      const uploadRes = await upload(cropFile, companyId);
      const fileId =
        typeof uploadRes?.data === "string"
          ? uploadRes.data
          : uploadRes?.data?.id;
      if (!fileId) throw new Error("Upload returned no fileId");
      await setUserAvatar(companyId, userId, fileId);
      toast.success(t("avatar_updated"));
      dispatch(fetchUserDetails({ companyId, userId }));
      dispatch(fetchOnlineThunk(companyId));
      dispatch(setIsModalxOpen(false));
    } catch (err: any) {
      // An empty file was already toasted by upload() — no second message.
      if (!isEmptyUploadError(err)) {
        toast.error(err?.response?.data?.error || t("avatar_upload_failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {!imageSrc ? (
        <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-sm text-gray-500 mb-3">
            {t("select_image_to_upload")}
          </p>
          <Button onClick={() => fileInputRef.current?.click()}>
            {t("choose_file")}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      ) : (
        <>
          {/* Half the screen at most, so a phone held sideways still has room to scroll the sheet. */}
          <div className="relative w-full h-[min(400px,50dvh)] bg-black rounded-lg overflow-hidden">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 w-16">{t("zoom")}</span>
            <input
              type="range"
              min={1}
              max={4}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1"
            />
          </div>

          <div className="flex flex-wrap justify-between gap-2">
            <Button
              onClick={() => {
                setImageSrc(null);
                setZoom(1);
                setCrop({ x: 0, y: 0 });
              }}
              disabled={saving}
            >
              {t("choose_another")}
            </Button>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => dispatch(setIsModalxOpen(false))}
                disabled={saving}
              >
                {t("cancel")}
              </Button>
              <Button variant="primary" onClick={handleSave} loading={saving}>
                {t("save")}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AvatarUploadModal;
